// React-Query-friendly HTTP shim. Each call resolves the Kinde
// access token (refreshing via re-login if needed) before forwarding
// to ``postJson``. All API hooks call through this so the
// ``Authorization: Bearer …`` header is consistent across the app.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useCallback, useContext } from "react";

import { getFreshToken, getTokenAfterRejection } from "../auth/freshToken";
import { anonymizeData } from "../demo/anonymize";
import { DEMO_BLOCKED_PATHS, DemoModeError, isDemoMode } from "../demo/demoMode";
import { AgentSlotContext, withSlot } from "./agentSlot";
import { postJson, type PostJsonError } from "./http/postJson";
import { useInrangeAgentsApiConfig } from "./use-config";

export type AgentsHttp = <TResp>(
  path: string,
  body?: unknown,
) => Promise<TResp>;

export const useAgentsHttp = (): AgentsHttp => {
  const { baseUrl } = useInrangeAgentsApiConfig();
  const { getToken } = useKindeAuth();
  const slotScope = useContext(AgentSlotContext);

  return useCallback(
    async <TResp>(path: string, unscoped?: unknown): Promise<TResp> => {
      // Demo mode allows the real flows (upload / run / converse) but blocks
      // the dangerous paths: anonymised write-back (corruption), destructive
      // ops, and raw-content exposure (see docs/Y2026-Q2/design-demo-mode.md).
      if (isDemoMode() && DEMO_BLOCKED_PATHS.has(path)) {
        throw new DemoModeError(path);
      }
      // A project page's slot reaches every call it makes from here, not from each hook.
      const body = withSlot(unscoped, slotScope);
      const token = await getFreshToken(getToken);
      let resp: TResp;
      try {
        resp = await postJson<TResp>(baseUrl, path, body, token);
      } catch (err) {
        if ((err as PostJsonError).status !== 401) throw err;
        const retry = await getTokenAfterRejection(token, getToken);
        if (retry === null) throw err;
        resp = await postJson<TResp>(baseUrl, path, body, retry);
      }
      // Anonymise every read response in one place so all surfaces are covered
      // at once. anonymizeData only rewrites known PII keys; ids/numbers pass
      // through untouched so routing is unaffected.
      return isDemoMode() ? anonymizeData(resp) : resp;
    },
    [baseUrl, getToken, slotScope],
  );
};
